import { Component, Input, OnChanges, SimpleChanges, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatButtonModule } from '@angular/material/button';
import {
  ParkingProcessService,
  ParkingProcessListItemDto,
  ListParkingProcessesRequest,
} from '../../../../api/gen';
import { ParkingZoneParkingProcessListComponent } from './parking-zone-parking-process-list/parking-zone-parking-process-list.component';
import { ResponsiveBaseComponent } from 'shared-lib';

export type ProcessFilterType = 'all' | 'active' | 'ended';

@Component({
  selector: 'app-parking-zone-parking-process',
  imports: [
    CommonModule,
    MatProgressBarModule,
    MatIconModule,
    MatButtonToggleModule,
    MatButtonModule,
    ParkingZoneParkingProcessListComponent,
  ],
  templateUrl: './parking-zone-parking-process.component.html',
  styleUrl: './parking-zone-parking-process.component.scss'
})
export class ParkingZoneParkingProcessComponent extends ResponsiveBaseComponent implements OnChanges {
  private parkingProcessService = inject(ParkingProcessService);

  @Input({ required: true }) zoneId!: string;
  @Input() zoneCapacity: number = 0;

  parkingProcesses = signal<ParkingProcessListItemDto[]>([]);
  filteredProcesses = signal<ParkingProcessListItemDto[]>([]);
  isLoading = signal(false);
  selectedFilter = signal<ProcessFilterType>('active');

  activeProcessCount = signal(0);
  occupancyPercentage = signal(0);

  isExpanded = signal(false);
  private hasLoaded = signal(false);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['zoneId']) {
      this.parkingProcesses.set([]);
      this.filteredProcesses.set([]);
      this.activeProcessCount.set(0);
      this.occupancyPercentage.set(0);
      this.hasLoaded.set(false);
      if (this.isExpanded()) {
        this.loadParkingProcesses();
      }
    }
  }

  toggleSection(): void {
    const expanding = !this.isExpanded();
    this.isExpanded.set(expanding);
    if (expanding && !this.hasLoaded()) {
      this.hasLoaded.set(true);
      this.loadParkingProcesses();
    }
  }

  loadParkingProcesses(): void {
    if (!this.zoneId) return;

    this.isLoading.set(true);
    const request: ListParkingProcessesRequest = {
      parkingZoneId: this.zoneId,
      pageSize: 100,
      pageIndex: 0,
      includeDeleted: false,
    };

    this.parkingProcessService.parkingProcessFilterEnrichedPost(request).subscribe({
      next: (result) => {
        const processes = result.items || [];
        this.parkingProcesses.set(processes);
        this.updateActiveCount(processes);
        this.applyFilter();
        this.isLoading.set(false);
      },
      error: (error) => {
        console.error('Error loading parking processes:', error);
        this.isLoading.set(false);
      }
    });
  }

  private updateActiveCount(processes: ParkingProcessListItemDto[]): void {
    const activeCount = processes.filter(p => p.isActive).length;
    this.activeProcessCount.set(activeCount);

    if (this.zoneCapacity > 0) {
      const percentage = Math.round((activeCount / this.zoneCapacity) * 100);
      this.occupancyPercentage.set(Math.min(percentage, 100));
    }
  }

  onFilterChange(filter: ProcessFilterType): void {
    this.selectedFilter.set(filter);
    this.applyFilter();
  }

  applyFilter(): void {
    const processes = this.parkingProcesses();
    const filter = this.selectedFilter();
    let result = processes;
    if (filter === 'active') result = result.filter(p => p.isActive);
    else if (filter === 'ended') result = result.filter(p => !p.isActive);
    this.filteredProcesses.set(result);
  }

  getOccupancyColor(): string {
    const pct = this.occupancyPercentage();
    if (pct >= 90) return 'text-red-600';
    if (pct >= 70) return 'text-yellow-600';
    return 'text-green-600';
  }

  getOccupancyChipClass(): string {
    const pct = this.occupancyPercentage();
    if (pct >= 90) return 'stat-chip danger-chip';
    if (pct >= 70) return 'stat-chip warn-chip';
    return 'stat-chip ok-chip';
  }
}

